import { BENCHED, queryFieldPositions } from './definitions'

type BuildRosterAnalysisProps = {
	roster: RosterData
	endRoster?: RosterData
	subs?: Subs
	players?: PlayerId[]
	numPeriods: NumPeriods
	gameFormat: GameFormat
}

type PairContribution = {
	qtr: number
	playerId1: PlayerId
	posIndex1: number
	playerId2: PlayerId
	posIndex2: number
	amount: number
}

type TrioContribution = {
	qtr: number
	playerId1: PlayerId
	posIndex1: number
	playerId2: PlayerId
	posIndex2: number
	playerId3: PlayerId
	posIndex3: number
	amount: number
}

type ForEachContributionProps = {
	onCourtRoster: RosterDataWithNullablesAndSubs
	numPeriods: NumPeriods
	gameFormat: GameFormat
	available?: Available
	qtr?: number
}

export function normalizeFieldPositionIndex(positionIndex: number, fieldPositions: readonly FieldPosition[]) {
	return fieldPositions[positionIndex] === fieldPositions[positionIndex + 1] ? positionIndex + 1 : positionIndex
}

function querySlotPosition(slotIndex: number, playersPerQtr: number, fieldPositions: readonly FieldPosition[]): FieldPosition {
	const posIndex = slotIndex % playersPerQtr
	return posIndex < fieldPositions.length ? fieldPositions[posIndex] : BENCHED
}

function querySlotPositionIndex(slotIndex: number, playersPerQtr: number, fieldPositions: readonly FieldPosition[]) {
	const posIndex = slotIndex % playersPerQtr
	if (posIndex >= fieldPositions.length) {
		return null
	}
	return normalizeFieldPositionIndex(posIndex, fieldPositions)
}

function queryHalfPlayer(playerArr: Array<PlayerId | null>, half: number) {
	if (!playerArr?.length) {
		return null
	}

	if (playerArr.length === 1) {
		return playerArr[0] || null
	}

	return playerArr[half] || null
}

function isAdjacentFieldPosition(posIndex1: number, posIndex2: number, gameFormat: GameFormat) {
	switch (gameFormat) {
		case 5:
			return posIndex1 === 2 || posIndex2 === 2 || Math.abs(posIndex2 - posIndex1) === 1
		case 6:
			return posIndex1 === 2 || posIndex2 === 2 || posIndex1 === 3 || posIndex2 === 3 || Math.abs(posIndex2 - posIndex1) === 1
		default:
			return Math.abs(posIndex2 - posIndex1) === 1
	}
}

function queryQuarterIndexes(numPeriods: number, qtr?: number) {
	if (typeof qtr === 'number') {
		return [ qtr ]
	}

	return [ ...Array(numPeriods).keys() ]
}

export function normalizeRosterSubs(
	subs: Subs | undefined,
	rosterLength: number,
	numPeriods: NumPeriods,
	gameFormat: GameFormat,
): Subs {
	if (!subs?.length || !rosterLength) {
		return [ ]
	}

	const fieldPositions = queryFieldPositions(gameFormat)
	const playersPerQtr = rosterLength / numPeriods
	const seen = new Set<number>()
	const pairs: Array<[number, number]> = [ ]

	for (let i = 0; i < subs.length; i += 2) {
		const rawIndex1 = subs[i]
		const rawIndex2 = subs[i + 1]

		if (!Number.isInteger(rawIndex1) || !Number.isInteger(rawIndex2)) {
			continue
		}

		if (
			rawIndex1 < 0 ||
			rawIndex2 < 0 ||
			rawIndex1 >= rosterLength ||
			rawIndex2 >= rosterLength ||
			rawIndex1 === rawIndex2
		) {
			continue
		}

		const qtr1 = Math.floor(rawIndex1 / playersPerQtr)
		const qtr2 = Math.floor(rawIndex2 / playersPerQtr)
		if (qtr1 !== qtr2) {
			continue
		}

		const pos1 = rawIndex1 % playersPerQtr
		const pos2 = rawIndex2 % playersPerQtr
		const index1IsField = pos1 < fieldPositions.length
		const index2IsField = pos2 < fieldPositions.length

		if (index1IsField === index2IsField) {
			continue
		}

		const fieldIndex = index1IsField ? rawIndex1 : rawIndex2
		const benchIndex = index1IsField ? rawIndex2 : rawIndex1

		if (seen.has(fieldIndex) || seen.has(benchIndex)) {
			continue
		}

		seen.add(fieldIndex)
		seen.add(benchIndex)
		pairs.push([ fieldIndex, benchIndex ])
	}

	pairs.sort((a, b) => a[0] - b[0] || a[1] - b[1])
	return pairs.flat()
}

export function buildLegacyEndRoster(
	roster: RosterData,
	subs: Subs | undefined,
	numPeriods: NumPeriods,
	gameFormat: GameFormat,
) {
	const endRoster = roster.slice()
	const normalizedSubs = normalizeRosterSubs(subs, roster.length, numPeriods, gameFormat)

	for (let i = 0; i < normalizedSubs.length; i += 2) {
		const index1 = normalizedSubs[i]
		const index2 = normalizedSubs[i + 1]
		;[ endRoster[index1], endRoster[index2] ] = [ endRoster[index2], endRoster[index1] ]
	}

	return endRoster
}

export function requiresPersistedEndRoster(
	roster: RosterData,
	endRoster: RosterData,
	subs: Subs | undefined,
	numPeriods: NumPeriods,
	gameFormat: GameFormat,
) {
	const legacyEndRoster = buildLegacyEndRoster(roster, subs, numPeriods, gameFormat)
	return endRoster.length !== legacyEndRoster.length || endRoster.some((playerId, index) => playerId !== legacyEndRoster[index])
}

export function validatedEndRoster(
	endRoster: RosterData | undefined,
	created: number | undefined,
	endRosterCreated: number | undefined,
) {
	return typeof created === 'number' && created === endRosterCreated ? endRoster : undefined
}

function haveSamePlayers(roster1: RosterData, roster2: RosterData) {
	if (roster1.length !== roster2.length) {
		return false
	}

	const counts = new Map<PlayerId, number>()
	roster1.forEach(playerId => counts.set(playerId, (counts.get(playerId) || 0) + 1))
	roster2.forEach(playerId => counts.set(playerId, (counts.get(playerId) || 0) - 1))
	return [ ...counts.values() ].every(count => count === 0)
}

// Bench slots are visually distinct but mean the same thing. Keep players who
// remain benched in their original slot, leaving the other slots to show the
// players who actually move on to or off the court.
function simplifyBenchTransitions(
	roster: RosterData,
	endRoster: RosterData,
	qtrStart: number,
	playersPerQtr: number,
	numFieldPositions: number,
) {
	const benchStart = qtrStart + numFieldPositions
	const qtrEnd = qtrStart + playersPerQtr
	const benchIndexes: number[] = [ ]
	for (let benchIndex = benchStart; benchIndex < qtrEnd; benchIndex += 1) {
		benchIndexes.push(benchIndex)
	}
	const originalEndPlayers = new Map(benchIndexes.map(benchIndex => [ benchIndex, endRoster[benchIndex] ]))
	const fixedIndexes = new Set<number>()
	const usedEndIndexes = new Set<number>()

	for (const benchIndex of benchIndexes) {
		const matchingEndIndex = benchIndexes.find(endIndex => (
			!usedEndIndexes.has(endIndex) && originalEndPlayers.get(endIndex) === roster[benchIndex]
		))
		if (matchingEndIndex !== undefined) {
			endRoster[benchIndex] = roster[benchIndex]
			fixedIndexes.add(benchIndex)
			usedEndIndexes.add(matchingEndIndex)
		}
	}

	const remainingEndPlayers = benchIndexes
		.filter(endIndex => !usedEndIndexes.has(endIndex))
		.map(endIndex => originalEndPlayers.get(endIndex) as PlayerId)
	let remainingIndex = 0
	for (const benchIndex of benchIndexes) {
		if (!fixedIndexes.has(benchIndex)) {
			endRoster[benchIndex] = remainingEndPlayers[remainingIndex]
			remainingIndex += 1
		}
	}
}

// Persisted endRoster values are trusted only when their endRosterCreated marker
// matches created; older clients update created without knowing about endRoster.
// Callers validate persisted data with validatedEndRoster() before passing it here.
// Local endRoster values are already trusted. Each period is still checked as a
// permutation so malformed data falls back to the legacy roster + subs model.
export function normalizeEndRoster(
	roster: RosterData,
	endRoster: RosterData | undefined,
	subs: Subs | undefined,
	numPeriods: NumPeriods,
	gameFormat: GameFormat,
) {
	const legacyEndRoster = buildLegacyEndRoster(roster, subs, numPeriods, gameFormat)
	if (
		!endRoster ||
		endRoster.length !== roster.length ||
		!roster.length
	) {
		return legacyEndRoster
	}

	const playersPerQtr = roster.length / numPeriods
	if (!Number.isInteger(playersPerQtr)) {
		return legacyEndRoster
	}

	const numFieldPositions = queryFieldPositions(gameFormat).length
	const normalized = endRoster.slice()
	for (let qtr = 0; qtr < numPeriods; qtr += 1) {
		const start = qtr * playersPerQtr
		const end = start + playersPerQtr
		if (!haveSamePlayers(roster.slice(start, end), normalized.slice(start, end))) {
			normalized.splice(start, playersPerQtr, ...legacyEndRoster.slice(start, end))
		}
		simplifyBenchTransitions(roster, normalized, start, playersPerQtr, numFieldPositions)
	}

	return normalized
}

// A normal drag moves the whole displayed tile, including any second-half
// position. Swap the start slots, then exchange the same two players wherever
// they appear in the end lineup for that period.
export function swapWholePositionTiles(
	roster: RosterData,
	endRoster: RosterData,
	from: RosterPositionIndex,
	to: RosterPositionIndex,
	numPeriods: NumPeriods,
) {
	const playersPerPeriod = roster.length / numPeriods
	const periodStart = Math.floor(from / playersPerPeriod) * playersPerPeriod
	const periodEnd = periodStart + playersPerPeriod
	const fromPlayer = roster[from]
	const toPlayer = roster[to]
	const swappedRoster = roster.slice()
	const swappedEndRoster = endRoster.slice()

	;[ swappedRoster[from], swappedRoster[to] ] = [ swappedRoster[to], swappedRoster[from] ]
	for (let i = periodStart; i < periodEnd; i += 1) {
		if (swappedEndRoster[i] === fromPlayer) {
			swappedEndRoster[i] = toPlayer
		} else if (swappedEndRoster[i] === toPlayer) {
			swappedEndRoster[i] = fromPlayer
		}
	}

	return { roster: swappedRoster, endRoster: swappedEndRoster }
}

// Older clients can only understand independent field/bench swaps. Preserve those
// where possible; positional rotations remain represented solely by endRoster.
export function deriveLegacySubs(
	roster: RosterData,
	endRoster: RosterData,
	numPeriods: NumPeriods,
	gameFormat: GameFormat,
): Subs {
	if (!roster.length || roster.length !== endRoster.length) {
		return [ ]
	}

	const fieldPositions = queryFieldPositions(gameFormat)
	const playersPerQtr = roster.length / numPeriods
	const subs: Subs = [ ]
	const visited = new Set<number>()

	for (let i = 0; i < roster.length; i += 1) {
		if (visited.has(i) || roster[i] === endRoster[i]) {
			continue
		}

		const qtrStart = Math.floor(i / playersPerQtr) * playersPerQtr
		const qtrEnd = qtrStart + playersPerQtr
		const partner = roster.findIndex((playerId, index) => (
			index >= qtrStart && index < qtrEnd && playerId === endRoster[i]
		))
		if (
			partner === -1 ||
			partner === i ||
			endRoster[partner] !== roster[i]
		) {
			continue
		}

		visited.add(i)
		visited.add(partner)
		const iIsField = i % playersPerQtr < fieldPositions.length
		const partnerIsField = partner % playersPerQtr < fieldPositions.length
		if (iIsField !== partnerIsField) {
			subs.push(...(iIsField ? [ i, partner ] : [ partner, i ]))
		}
	}

	return normalizeRosterSubs(subs, roster.length, numPeriods, gameFormat)
}

export function countFieldBenchTransitions(
	roster: RosterData,
	endRoster: RosterData,
	numPeriods: NumPeriods,
	gameFormat: GameFormat,
) {
	if (!roster.length || roster.length !== endRoster.length) {
		return 0
	}

	const playersPerQtr = roster.length / numPeriods
	const numFieldPositions = queryFieldPositions(gameFormat).length
	let count = 0

	for (let qtr = 0; qtr < numPeriods; qtr += 1) {
		const start = qtr * playersPerQtr
		const fieldEnd = start + numFieldPositions
		const endFieldPlayers = new Set(endRoster.slice(start, fieldEnd))
		for (let fieldIndex = start; fieldIndex < fieldEnd; fieldIndex += 1) {
			if (!endFieldPlayers.has(roster[fieldIndex])) {
				count += 1
			}
		}
	}

	return count
}

export function buildRosterAnalysis(props: BuildRosterAnalysisProps) {
	const { roster, endRoster, subs, players, numPeriods, gameFormat } = props
	const fieldPositions = queryFieldPositions(gameFormat)
	const normalizedEndRoster = normalizeEndRoster(roster, endRoster, subs, numPeriods, gameFormat)
	const normalizedSubs = deriveLegacySubs(roster, normalizedEndRoster, numPeriods, gameFormat)
	const playersPerQtr = roster.length / numPeriods
	const onCourtRoster: RosterDataWithNullablesAndSubs = roster.map((playerId, index) => {
		const endPlayerId = normalizedEndRoster[index]
		return playerId === endPlayerId ? [ playerId || null ] : [ playerId || null, endPlayerId || null ]
	})
	const startPlayersByQuarter = new Array(numPeriods).fill(null).map(() => new Array<PlayerId | null>(fieldPositions.length).fill(null))
	const endPlayersByQuarter = new Array(numPeriods).fill(null).map(() => new Array<PlayerId | null>(fieldPositions.length).fill(null))
	const byPlayer: {
		[key: string]: {
			quarters: Array<{
				segments: Array<{
					position: FieldPosition
					positionIndex: number | null
					amount: number
				}>
				startPosition: FieldPosition
				endPosition: FieldPosition
				displayPosition: FieldPosition
				benchAmount: number
			}>
		}
	} = { }

	const playerIds = players ? players.slice() : [ ]
	const seenPlayers = new Set(playerIds)
	roster.forEach(playerId => {
		if (playerId && !seenPlayers.has(playerId)) {
			seenPlayers.add(playerId)
			playerIds.push(playerId)
		}
	})
	normalizedEndRoster.forEach(playerId => {
		if (playerId && !seenPlayers.has(playerId)) {
			seenPlayers.add(playerId)
			playerIds.push(playerId)
		}
	})

	playerIds.forEach(playerId => {
		byPlayer[playerId] = {
			quarters: new Array(numPeriods),
		}
	})

	for (const playerId of playerIds) {
		if (!playerId) {
			continue
		}

		for (let qtr = 0; qtr < numPeriods; qtr += 1) {
			const qtrStart = qtr * playersPerQtr
			const qtrEnd = qtrStart + playersPerQtr
			const startIndex = roster.findIndex((candidate, index) => index >= qtrStart && index < qtrEnd && candidate === playerId)
			const endIndex = normalizedEndRoster.findIndex((candidate, index) => index >= qtrStart && index < qtrEnd && candidate === playerId)
			if (startIndex === -1 && endIndex === -1) {
				continue
			}

			const startPosition = startIndex === -1 ? BENCHED : querySlotPosition(startIndex, playersPerQtr, fieldPositions)
			const startPositionIndex = startIndex === -1 ? null : querySlotPositionIndex(startIndex, playersPerQtr, fieldPositions)
			const endPosition = endIndex === -1 ? BENCHED : querySlotPosition(endIndex, playersPerQtr, fieldPositions)
			const endPositionIndex = endIndex === -1 ? null : querySlotPositionIndex(endIndex, playersPerQtr, fieldPositions)
			const segments = startPosition === endPosition && startPositionIndex === endPositionIndex ? [ {
				position: startPosition,
				positionIndex: startPositionIndex,
				amount: 1,
			} ] : [
				{ position: startPosition, positionIndex: startPositionIndex, amount: 0.5 },
				{ position: endPosition, positionIndex: endPositionIndex, amount: 0.5 },
			]
			const displaySegment = segments.find(segment => segment.position !== BENCHED)
			byPlayer[playerId].quarters[qtr] = {
				segments,
				startPosition,
				endPosition,
				displayPosition: displaySegment?.position || BENCHED,
				benchAmount: segments.reduce((total, segment) => total + (segment.position === BENCHED ? segment.amount : 0), 0),
			}
		}
	}

	for (let qtr = 0; qtr < numPeriods; qtr += 1) {
		const qtrStart = qtr * playersPerQtr
		for (let posIndex = 0; posIndex < fieldPositions.length; posIndex += 1) {
			startPlayersByQuarter[qtr][normalizeFieldPositionIndex(posIndex, fieldPositions)] = roster[qtrStart + posIndex] || null
			endPlayersByQuarter[qtr][normalizeFieldPositionIndex(posIndex, fieldPositions)] = normalizedEndRoster[qtrStart + posIndex] || null
		}
	}

	Object.keys(byPlayer).forEach(playerId => {
		for (let qtr = 0; qtr < numPeriods; qtr += 1) {
			if (!byPlayer[playerId].quarters[qtr]) {
				byPlayer[playerId].quarters[qtr] = {
					segments: [
						{
							position: BENCHED,
							positionIndex: null,
							amount: 1,
						},
					],
					startPosition: BENCHED,
					endPosition: BENCHED,
					displayPosition: BENCHED,
					benchAmount: 1,
				}
			}
		}
	})

	return {
		byPlayer,
		onCourtRoster,
		endRoster: normalizedEndRoster,
		normalizedSubs,
		startPlayersByQuarter,
		endPlayersByQuarter,
	}
}

export function forEachPairContribution(props: ForEachContributionProps, callback: (contribution: PairContribution) => void) {
	const { onCourtRoster, numPeriods, gameFormat, available, qtr } = props
	const fieldPositions = queryFieldPositions(gameFormat)
	const playersPerQtr = onCourtRoster.length / numPeriods

	queryQuarterIndexes(numPeriods, qtr).forEach(currentQtr => {
		const qtrRoster = onCourtRoster.slice(currentQtr * playersPerQtr, (currentQtr + 1) * playersPerQtr)

		for (let half = 0; half < 2; half += 1) {
			const halfPlayers = qtrRoster.map(playerArr => queryHalfPlayer(playerArr, half))

			for (let posIndex1 = 0; posIndex1 < fieldPositions.length - 1; posIndex1 += 1) {
				const playerId1 = halfPlayers[posIndex1]
				if (!playerId1 || (available && !available[playerId1])) {
					continue
				}

				for (let posIndex2 = posIndex1 + 1; posIndex2 < fieldPositions.length; posIndex2 += 1) {
					if (!isAdjacentFieldPosition(posIndex1, posIndex2, gameFormat)) {
						continue
					}

					const playerId2 = halfPlayers[posIndex2]
					if (!playerId2 || (available && !available[playerId2])) {
						continue
					}

					callback({
						qtr: currentQtr,
						playerId1,
						posIndex1,
						playerId2,
						posIndex2,
						amount: 0.5,
					})
				}
			}
		}
	})
}

export function forEachTrioContribution(props: ForEachContributionProps, callback: (contribution: TrioContribution) => void) {
	const { onCourtRoster, numPeriods, gameFormat, available, qtr } = props
	const fieldPositions = queryFieldPositions(gameFormat)
	const playersPerQtr = onCourtRoster.length / numPeriods

	queryQuarterIndexes(numPeriods, qtr).forEach(currentQtr => {
		const qtrRoster = onCourtRoster.slice(currentQtr * playersPerQtr, (currentQtr + 1) * playersPerQtr)

		for (let half = 0; half < 2; half += 1) {
			const halfPlayers = qtrRoster.map(playerArr => queryHalfPlayer(playerArr, half))

			for (let posIndex1 = 0; posIndex1 < fieldPositions.length - 1; posIndex1 += 1) {
				const playerId1 = halfPlayers[posIndex1]
				if (!playerId1 || (available && !available[playerId1])) {
					continue
				}

				for (let posIndex2 = posIndex1 + 1; posIndex2 < fieldPositions.length; posIndex2 += 1) {
					if (!isAdjacentFieldPosition(posIndex1, posIndex2, gameFormat)) {
						continue
					}

					const playerId2 = halfPlayers[posIndex2]
					if (!playerId2 || (available && !available[playerId2])) {
						continue
					}

					for (let posIndex3 = posIndex2 + 1; posIndex3 < fieldPositions.length; posIndex3 += 1) {
						if (
							!isAdjacentFieldPosition(posIndex3, posIndex2, gameFormat) &&
							!isAdjacentFieldPosition(posIndex3, posIndex1, gameFormat)
						) {
							continue
						}

						const playerId3 = halfPlayers[posIndex3]
						if (!playerId3 || (available && !available[playerId3])) {
							continue
						}

						callback({
							qtr: currentQtr,
							playerId1,
							posIndex1,
							playerId2,
							posIndex2,
							playerId3,
							posIndex3,
							amount: 0.5,
						})
					}
				}
			}
		}
	})
}
